import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, CheckCircle2, AlertCircle, Link2, Unlink, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * MyGmailConnection — drop-in card for the My Profile page.
 *
 * Lets a user connect their own @company.com inbox via Google OAuth.
 * Once connected, the inbox is available in the Communications mailbox
 * switcher AND becomes the sender for the Reports "Send Email" feature.
 */
export default function MyGmailConnection() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/communications/personal/connection-status`);
      setStatus(res.data);
    } catch (e) {
      console.error('Failed to fetch personal mailbox status:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // OAuth callback handling — when the user returns from Google to /communications
  // we surface a toast there. This component is on /my-profile so we only need
  // to refresh status on mount.

  const handleConnect = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Please sign in again before connecting Gmail');
      return;
    }
    window.location.href = `${API}/oauth/gmail/personal/start?token=${encodeURIComponent(token)}`;
  };

  const handleDisconnect = async () => {
    if (!window.confirm(
      'Disconnect your personal Gmail? You will no longer be able to send from your own identity inside the app. ' +
      'To fully revoke access in Google, visit https://myaccount.google.com/permissions.'
    )) return;
    setActing(true);
    try {
      await axios.post(`${API}/communications/personal/disconnect`);
      toast.success('Personal Gmail disconnected');
      await fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to disconnect');
    } finally {
      setActing(false);
    }
  };

  const connectedAt = status?.connected_at
    ? new Date(status.connected_at).toLocaleString()
    : null;

  return (
    <Card className="p-6" data-testid="my-gmail-connection">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div className="flex items-start gap-3">
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${
            status?.connected ? 'bg-emerald-100' : 'bg-muted'
          }`}>
            <Mail size={20} className={status?.connected ? 'text-emerald-600' : 'text-muted-foreground'} />
          </div>
          <div>
            <h3 className="font-semibold">My Gmail</h3>
            <p className="text-sm text-muted-foreground mt-0.5">
              Connect your own work Gmail so emails you send through the app (reports, replies, etc.) come from <span className="font-semibold text-foreground">your own address</span> and land in your real Sent folder.
            </p>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={fetchStatus} disabled={loading} data-testid="refresh-my-gmail-status">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Checking connection…</p>
      ) : status?.connected ? (
        <div className="p-4 rounded-lg border bg-emerald-50/50 border-emerald-200">
          <div className="flex items-center gap-2 text-emerald-700">
            <CheckCircle2 size={18} />
            <span className="font-semibold">Connected</span>
          </div>
          <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <div className="flex gap-2">
              <dt className="text-muted-foreground min-w-24">Email</dt>
              <dd className="font-mono">{status.email}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="text-muted-foreground min-w-24">Connected</dt>
              <dd>{connectedAt || '—'}</dd>
            </div>
          </dl>
          <div className="flex flex-wrap gap-2 mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={handleConnect}
              disabled={acting}
              data-testid="reconnect-my-gmail-btn"
            >
              <Link2 size={14} className="mr-2" />
              Reconnect / Switch account
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDisconnect}
              disabled={acting}
              className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
              data-testid="disconnect-my-gmail-btn"
            >
              <Unlink size={14} className="mr-2" />
              Disconnect
            </Button>
          </div>
        </div>
      ) : (
        <div className="p-4 rounded-lg border bg-muted/20">
          <div className="flex items-center gap-2 text-muted-foreground mb-2">
            <AlertCircle size={16} />
            <span className="font-medium text-sm">Not connected</span>
          </div>
          <p className="text-sm text-muted-foreground mb-3">
            When connected, any email you send through the app — including report PDFs from the Map and Reports pages — will be sent from your own Gmail with your name in the From line.
          </p>
          <Button onClick={handleConnect} disabled={acting} data-testid="connect-my-gmail-btn">
            <Link2 size={16} className="mr-2" />
            Connect My Gmail
          </Button>
        </div>
      )}
    </Card>
  );
}
